/**
 * assessmentSuccessionPlan object definition (model used by API)
 * @typedef {Object} assessmentSuccessionPlan
 * @property {int} assessmentProcessId
 * @property {int} assessmentProcessSuccessionPlanId
 * @property {?int} assessmentVendorResourceId
 * @property {?string} assessmentVendorResourceName
 * @property {?date} createdDate
 * @property {?int} createdUserId
 * @property {int} planPriority
 * @property {?int} userId
 * @property {?string} userName
 */

/**
 * @typedef {Array<assessmentSuccessionPlan>} assessmentSuccessionPlans
 */

/**
 * Successor object definition (model used by view)
 * @typedef {Object} successor
 * @property {?int} assessmentProcessId
 * @property {?int} assessmentProcessSuccessionPlanId
 * @property {bool} isVendor
 * @property {string} name
 * @property {?int} planPriority
 * @property {?int} userId
 * @property {?int} vendorResourceId
 */

/**
 * Successions object definition (model used by view)
 * @typedef {Object} successions
 * @property {?successor} primary
 * @property {?successor} alternate
 * @property {?successor} tertiary
 */

export default class bcpSharedModelTransformations {
  /**
   * Converts 'successions' viewmodel to 'assessmentSuccessionPlans' API model
   * @param {successions} successions
   * @return {Array<assessmentSuccessionPlan>}
   */
  static transformSuccessionsToAssessmentSuccessionPlans(successions) {
    /**
     * @param {successor} successor
     * @return {assessmentSuccessionPlan}
     */
    const transform = successor => ({
      assessmentProcessId: successor.assessmentProcessId,
      assessmentProcessSuccessionPlanId: successor.assessmentProcessSuccessionPlanId,
      assessmentVendorResourceId: successor.vendorResourceId,
      assessmentVendorResourceName: successor.isVendor ? successor.name : '',
      planPriority: successor.planPriority,
      userId: successor.userId,
      userName: !successor.isVendor ? successor.name : '',
    });
    const apsp = [];
    if (successions.primary) {
      apsp.push(transform(successions.primary));
    }
    if (successions.alternate) {
      apsp.push(transform(successions.alternate));
    }
    if (successions.tertiary) {
      apsp.push(transform(successions.tertiary));
    }
    return apsp;
  }

  /**
   * Converts 'assessmentSuccessionPlans' API model to 'successions' viewmodel
   * @param {Array<assessmentSuccessionPlan>} assessmentSuccessionPlans
   * @return {successions}
   */
  static transformAssessmentSuccessionPlansToSuccessions(assessmentSuccessionPlans) {
    /**
     * @param {assessmentSuccessionPlan} apiModel
     * @param {int} planPriority
     * @param {bool} isVendor
     * @return {successor}
     */
    const transform = (apiModel, planPriority, isVendor) => ({
      assessmentProcessId: apiModel.assessmentProcessId,
      assessmentProcessSuccessionPlanId: apiModel.assessmentProcessSuccessionPlanId,
      isVendor,
      name: apiModel.vendorName ? apiModel.vendorName : apiModel.userName,
      planPriority,
      userId: apiModel.userId,
      vendorId: apiModel.vendorId,
    });

    /**
     * @type {successions}
     */
    const successions = {
      primary: null,
      alternate: null,
      tertiary: null,
    };

    if (assessmentSuccessionPlans && assessmentSuccessionPlans.length !== 0) {
      assessmentSuccessionPlans.forEach((item) => {
        const isVendor = !!item.vendorId;
        switch (item.planPriority) {
          case 1:
            successions.primary = transform(item, 1, false);
            break;
          case 2:
            successions.alternate = transform(item, 2, isVendor);
            break;
          case 3:
            successions.tertiary = transform(item, 3, isVendor);
            break;
          default:
        }
      });
    }
    return successions;
  }

  /**
   * Converts 'vendorResources' API model to an array of 'successor' viewmodels
   * @param {int} assessmentProcessId
   * @param {int} assessmentProcessSuccessionPlanId
   * @param {Array} vendorResources
   * @return {Array<successor>}
   */
  static transformVendorResourceToSuccessor(assessmentProcessId, assessmentProcessSuccessionPlanId, vendorResources) {
    /**
     * @param {Object} vendor
     * @return {successor}
     */
    const transform = vendorResource => ({
      assessmentProcessId,
      assessmentProcessSuccessionPlanId,
      isVendor: true,
      name: vendorResource.vendor.name,
      planPriority: null,
      userId: null,
      vendorResourceId: vendorResource.assessmentVendorResourceId,
    });
    const successors = [];
    vendorResources.forEach(vR => successors.push(transform(vR)));
    return successors;
  }

  /**
   * Converts 'internalUsers' API model to an array of 'successor' viewmodels
   * @param {int} assessmentProcessId
   * @param {int} assessmentProcessSuccessionPlanId
   * @param {Array} internalUsers
   * @return {Array<successor>}
   */
  static transformInternalUsersToSuccessors(assessmentProcessId, assessmentProcessSuccessionPlanId, internalUsers) {
    /**
     * @param {Object} vendor
     * @return {successor}
     */
    const transform = internalUser => ({
      assessmentProcessId,
      assessmentProcessSuccessionPlanId,
      isVendor: false,
      name: internalUser.user.displayName,
      planPriority: null,
      userId: internalUser.userId,
      vendorResourceId: null,
    });
    const successors = [];
    internalUsers.forEach(iU => successors.push(transform(iU)));
    return successors;
  }

  /**
   * Converts 'internalUsers' API model to an array of 'successor' viewmodels
   * @param {int} assessmentProcessId
   * @param {int} assessmentProcessSuccessionPlanId
   * @param {Array} internalUsers
   * @return {Array<successor>}
   */

  static transformUsersToSuccessors(assessmentProcessId, assessmentProcessSuccessionPlanId, users) {
    /**
     * @param {Object} user
     * @return {successor}
     */
    const transform = user => ({
      assessmentProcessId,
      assessmentProcessSuccessionPlanId,
      isVendor: false,
      name: user.displayName,
      planPriority: null,
      userId: user.userId,
      vendorResourceId: null,
    });
    const successors = [];
    users.forEach(u => successors.push(transform(u)));
    return successors;
  }
}
